/** @jsxImportSource theme-ui */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  ButtonGroup,
  SelectButton,
  Button,
  Avatar,
  Text,
} from '@infotrack/zenith-ui';

import { useDashboardData } from '../hooks/useDashboardData';
import { useDashboardFilters, FilterState } from '../hooks/useDashboardFilters';
import { dashboardHeaderStyles } from './dashboardHeader.styles';

type PeriodType = 'current' | 'previous';

interface DashboardHeaderProps {
  resultCount?: number;
  onPeriodChange?: (period: PeriodType) => void;
  onFiltersChange?: (filters: FilterState) => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = React.memo(
  function DashboardHeader({
    resultCount = 3,
    onPeriodChange,
    onFiltersChange,
  }) {
    const { displayName } = useDashboardData();
    const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('current');
    const {
      filters,
      handleFilterChange,
      clearFilters,
      hasActiveFilters,
      filtersKey,
    } = useDashboardFilters(onFiltersChange);

    const handlePeriodChange = useCallback(
      (param: unknown) => {
        // Handle different possible parameter structures from ButtonGroup
        let period: PeriodType;
        if (typeof param === 'object' && param !== null && 'value' in param) {
          period = (param as { value: PeriodType }).value;
        } else {
          period = param as PeriodType;
        }
        setSelectedPeriod(period);
        onPeriodChange?.(period);
      },
      [onPeriodChange]
    );

    const handlePeopleFilterChange = useCallback(
      (params: unknown) => {
        const paramsObj = params as {
          selectedOption?: { value?: string };
          option?: { value?: string };
          value?: string;
        };
        const selectedValue =
          paramsObj?.selectedOption?.value ||
          paramsObj?.option?.value ||
          paramsObj?.value;
        if (selectedValue) {
          handleFilterChange('people', selectedValue);
        }
      },
      [handleFilterChange]
    );

    const handleTopFiveFilterChange = useCallback(
      (params: unknown) => {
        const paramsObj = params as {
          selectedOption?: { value?: string };
          option?: { value?: string };
          value?: string;
        };
        const selectedValue =
          paramsObj?.selectedOption?.value ||
          paramsObj?.option?.value ||
          paramsObj?.value;
        if (selectedValue) {
          handleFilterChange('topFive', selectedValue);
        }
      },
      [handleFilterChange]
    );

    // Memoize options arrays
    const periodOptions = useMemo(
      () => [
        { text: 'Previous', value: 'previous' as const },
        { text: 'Current', value: 'current' as const },
      ],
      []
    );

    const peopleOptions = useMemo(
      () => [
        { text: 'All People', value: 'all' },
        { text: 'Active Users', value: 'active' },
        { text: 'Inactive Users', value: 'inactive' },
      ],
      []
    );

    const topFiveOptions = useMemo(
      () => [
        { text: 'Top 5 Overall', value: 'overall' },
        { text: 'Top 5 This Month', value: 'month' },
        { text: 'Top 5 This Week', value: 'week' },
      ],
      []
    );

    return (
      <Box as="header" role="banner" sx={dashboardHeaderStyles.headerContainer}>
        <Box sx={dashboardHeaderStyles.leftSection}>
          {/* Logo */}
          <Box sx={dashboardHeaderStyles.logo}>
            <Text variant="hero" as="h1">
              iTop5
            </Text>
          </Box>

          {/* Filters Section */}
          <Box
            sx={dashboardHeaderStyles.filtersSection}
            role="toolbar"
            aria-label="Dashboard filters"
          >
            {/* Period Toggle */}
            <ButtonGroup
              options={periodOptions}
              value={selectedPeriod}
              onValueChange={handlePeriodChange}
            />

            {/* Filter Selects */}
            <Box sx={dashboardHeaderStyles.selectsContainer}>
              {/* People Filter */}
              <SelectButton
                key={`people-${filtersKey}`}
                placeholder="People"
                value={filters.people}
                options={peopleOptions}
                onSelect={handlePeopleFilterChange}
              />

              {/* Top 5 Filter */}
              <SelectButton
                key={`topfive-${filtersKey}`}
                placeholder="Top 5"
                value={filters.topFive}
                options={topFiveOptions}
                onSelect={handleTopFiveFilterChange}
              />

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={clearFilters}
                  aria-label="Clear all filters"
                >
                  Clear filters
                </Button>
              )}
            </Box>
          </Box>

          {/* Results Count */}
          <Box
            role="status"
            aria-live="polite"
            sx={dashboardHeaderStyles.resultsText}
          >
            <Text variant="caption">Displaying {resultCount} results</Text>
          </Box>
        </Box>

        {/* Right Section - User Name and Avatar */}
        <Box sx={dashboardHeaderStyles.rightSection}>
          <Avatar name={displayName} />
        </Box>
      </Box>
    );
  }
);
